import React, { FC, createContext, useContext, useEffect, useRef, useState } from 'react'
import { RefinementListProvided } from 'react-instantsearch-core'
import { connectRange, connectRefinementList } from 'react-instantsearch-dom'

import { RangeSliderPropType, RangeSliderValuesType } from '../RecipesInspirationPageTypes'

import { DietIcon } from './DietIcon'
import RecipeCategoryTitles from './RecipeCategoryTitles'

import { FormInputRange } from 'Components'
import { coreBackendService } from 'Services'
import { Formatter } from 'Utils'

export const FilterResetContext = createContext(0)

export const RecipesInspirationFilter: React.FC = () => {
  return (
    <div className="col-12 mx-2 mt-2">
      <div className="row filter-container mt-0 pt-2">
        <div className="col-12 col-lg-4 px-2 mt-0">
          <CustomRangeSlider
            attribute={'nutritionsPerPortion.energy'}
            rangeSliderValues={getEnergyPerPortionSliderValues()}
          />
        </div>

        <div className="col-12 col-lg-4 px-2 mt-0">
          <CustomRangeSlider attribute={'preparationTime'} rangeSliderValues={getPreparationTimeSliderValues()} />
        </div>

        <div className="col-12 col-lg-4 px-2 pb-1 mt-0">
          <div className="filter-container-title mb-1">Ernährungsvariante</div>

          <CustomRefinementList attribute="diet" operator="or" />
        </div>
      </div>

      <div className="row filter-container mt-2">
        <div className="col-12 mt-0">
          <CustomRefinementButtons attribute="recipeCategoryNames" operator="or" limit={1000} />
        </div>
      </div>
    </div>
  )
}

const RangeSlider: FC<RangeSliderPropType> = ({ max: statsMax, rangeSliderValues, refine }) => {
  const { label, defaultValue, maximumValue, minimumValue, inputStep } = rangeSliderValues

  const filterResetVersion = useContext(FilterResetContext)

  const [value, setValue] = useState(defaultValue)

  const refineRef = useRef(refine)

  refineRef.current = refine

  useEffect(() => {
    if (filterResetVersion === 0) {
      return
    }
    setValue(defaultValue)
    refineRef.current({})
  }, [filterResetVersion, defaultValue])

  const onChange = (nextValue: number): void => {
    const effectiveMax = statsMax !== undefined ? Math.min(maximumValue, statsMax) : maximumValue

    if (nextValue >= effectiveMax) {
      setValue(defaultValue)
      refine({})
      return
    }

    setValue(nextValue)
    refine({ max: nextValue })
  }

  return (
    <>
      <div className="filter-container-title mb-1">
        <span>{label}</span>
      </div>
      <div className="mr-2">
        <FormInputRange
          value={value}
          maximumValue={maximumValue}
          minimumValue={minimumValue}
          onChange={onChange}
          renderValue={(rangeValue) => (rangeValue === maximumValue ? `${rangeValue}+` : `${rangeValue}`)}
          inputStep={inputStep}
        />
      </div>
    </>
  )
}

const getPreparationTimeSliderValues = (): RangeSliderValuesType => {
  return {
    label: 'Zubereitungszeit (max.)',
    defaultValue: 120,
    maximumValue: 120,
    minimumValue: 5,
    inputStep: '5'
  }
}

const getEnergyPerPortionSliderValues = (): RangeSliderValuesType => {
  return {
    label: 'Kalorien pro Portion (max.)',
    defaultValue: 600,
    maximumValue: 600,
    minimumValue: 50,
    inputStep: '50'
  }
}

const displayNoRefinementsText = (): React.ReactElement => {
  return <div>Kein Filter möglich</div>
}

const CustomRangeSlider = connectRange(RangeSlider)

const RefinementList: FC<RefinementListProvided> = ({ items, refine }) => {
  items.sort((a, b) => a.label.localeCompare(b.label))

  const displayRefinements = (): React.ReactElement => {
    return (
      <ul>
        {items.map((item) => (
          <li key={item.label}>
            <label key={item.label} className="checkbox">
              <input value={item.label} type="checkbox" checked={item.isRefined} onChange={() => refine(item.value)} />
              <span className="label">
                <DietIcon diet={item.label} decorative />
                {item.label} ({Formatter.formatNumber(item.count)})
              </span>
            </label>
          </li>
        ))}
      </ul>
    )
  }
  return <div className="diet-filter">{items.length === 0 ? displayNoRefinementsText() : displayRefinements()}</div>
}

const RefinementButtons: FC<RefinementListProvided> = ({ items, refine }) => {
  const [publishedRecipeCategoryNames, setPublishedRecipeCategoryNames] = React.useState<string[]>([])

  useEffect(() => {
    const fetchRecipeCategories = async (): Promise<void> => {
      const RecipeCategories = await coreBackendService.fetchPublishedRecipeCategories()
      setPublishedRecipeCategoryNames(RecipeCategories.map((category) => category.name))
    }
    fetchRecipeCategories()
  }, [])

  const categoryTitle = (name: string): string =>
    RecipeCategoryTitles[name as keyof typeof RecipeCategoryTitles] ?? name

  const allPublishedCategories = publishedRecipeCategoryNames
    .map((name) => ({
      label: name,
      title: categoryTitle(name)
    }))
    .sort((a, b) => a.title.localeCompare(b.title))

  const availableCategories = items
    .filter((item) => publishedRecipeCategoryNames.includes(item.label))
    .map((item) => ({
      ...item,
      title: categoryTitle(item.label)
    }))
    .sort((a, b) => a.title.localeCompare(b.title))

  return (
    <div className={`mt-2 category-filter${allPublishedCategories.length > 0 ? ' category-filter-sized' : ''}`}>
      {allPublishedCategories.length > 0 && (
        <div className="category-filter-sizer" aria-hidden="true">
          {allPublishedCategories.map((category) => (
            <input
              key={category.label}
              value={category.title}
              type="button"
              tabIndex={-1}
              className="recipe-category-button"
            />
          ))}
        </div>
      )}

      <div className="category-filter-content">
        {availableCategories.length === 0
          ? displayNoRefinementsText()
          : availableCategories.map((category) => (
              <input
                key={category.label}
                value={category.title}
                type="button"
                onClick={() => {
                  refine(category.value)
                }}
                className={`${category.isRefined ? 'recipe-category-button-active' : ''} recipe-category-button`}
              />
            ))}
      </div>
    </div>
  )
}

const CustomRefinementList = connectRefinementList(RefinementList)

const CustomRefinementButtons = connectRefinementList(RefinementButtons)
